import { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchContentById, clearCurrent, recordView, toggleLike } from '../store/contentSlice';
import { fetchPurchases } from '../store/paymentSlice';
import { TYPE_LABELS } from '../data/catalog';
import PaywallOverlay from '../components/PaywallOverlay';
import { resolveMediaUrl, getThumbnailUrl, getAvatarUrl } from '../utils/media';
import { useThumbnailAuth } from '../hooks/useThumbnailAuth';
import { useAvatarAuth } from '../hooks/useAvatarAuth';
import { timeAgo, formatCount } from '../utils/dates';
import api from '../api';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';


export default function ContentView() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { current, loading } = useSelector((state) => state.content);
  const { user } = useSelector((state) => state.auth);
  const { purchases } = useSelector((state) => state.payment);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const canWatchRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [paywalled, setPaywalled] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [related, setRelated] = useState([]);
  const [showFullDesc, setShowFullDesc] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const [showCopied, setShowCopied] = useState(false);
  const [playerContainer, setPlayerContainer] = useState(null);
  const [streamUrl, setStreamUrl] = useState(null);
  const thumbAuth = useThumbnailAuth();
  const avatarAuth = useAvatarAuth();

  const content = current;

  const purchased = useMemo(() =>
    purchases.some(p => String(p.contentId) === String(id) || String(p.content_id) === String(id)),
    [purchases, id]
  );

  useEffect(() => {
    dispatch(fetchContentById(id));
    return () => dispatch(clearCurrent());
  }, [dispatch, id]);

  // Record view once content is loaded
  useEffect(() => {
    if (current) {
      dispatch(recordView(id));
    }
  }, [dispatch, id, current?.id]);

  // Fetch related content
  useEffect(() => {
    if (!content?.type) return;
    api.get('/content', { params: { type: content.type, limit: 9 } })
      .then(({ data }) => setRelated(data.filter(c => String(c.id) !== String(id)).slice(0, 8)))
      .catch(() => setRelated([]));
  }, [content?.type, id]);

  useEffect(() => {
    if (user) dispatch(fetchPurchases());
  }, [dispatch, user]);

  const isOwner = user && content?.creator_id && user.id === content.creator_id;
  const canWatch = purchased || isOwner || content?.hasAccess;
  canWatchRef.current = canWatch;

  // Auto-dismiss paywall when purchase is confirmed via Redux
  useEffect(() => {
    if (paywalled && canWatch) {
      canWatchRef.current = true;
      setPaywalled(false);
      const player = playerRef.current;
      if (player) setTimeout(() => player.play().catch(() => {}), 100);
    }
  }, [paywalled, canWatch]);

  // Reset state on content change
  useEffect(() => {
    setPlaying(false);
    setPaywalled(false);
    setShowFullDesc(false);
    setComments([]);
    setStreamUrl(null);
  }, [id]);

  // Fetch stream URL (signed B2 URL or legacy local path)
  useEffect(() => {
    if (!playing || !content?.media_url) return;
    // Legacy local files — resolve directly
    if (content.media_url.startsWith('/uploads/')) {
      setStreamUrl(resolveMediaUrl(content.media_url));
      return;
    }
    // B2 content — fetch signed URL (works with or without auth)
    let cancelled = false;
    api.get(`/content/${id}/stream`)
      .then(({ data }) => { if (!cancelled) setStreamUrl(data.url); })
      .catch(() => {
        if (!cancelled) setStreamUrl(null);
      });
    return () => { cancelled = true; };
  }, [playing, content?.media_url, id]);

  // Initialize Video.js player
  useEffect(() => {
    if (!playing || !streamUrl) return;
    if (!videoRef.current) return;

    const player = videojs(videoRef.current, {
      controls: true,
      autoplay: true,
      fill: true,
      playbackRates: [0.5, 1, 1.5, 2],
      sources: [{ src: streamUrl, type: 'video/mp4' }],
    });

    // Gradient definition shared across all icons
    const grad = '<defs><linearGradient id="vjs-grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#C8963E"/><stop offset="100%" stop-color="#D2B48C"/></linearGradient></defs>';
    const g = 'url(#vjs-grad)';

    // Quality selector button
    const MenuButton = videojs.getComponent('MenuButton');
    const MenuItem = videojs.getComponent('MenuItem');
    const qualities = ['Auto', '1080p', '720p', '480p', '360p'];

    class QualityMenuItem extends MenuItem {
      constructor(p, options) {
        super(p, { ...options, selectable: true, multiSelectable: false });
        this.selected(options.label === 'Auto');
      }
      handleClick() {
        this.options_.parent.items().forEach(i => i.selected(false));
        this.selected(true);
        this.options_.parent.el().querySelector('.vjs-quality-label').textContent = this.options_.label;
      }
    }

    class QualityButton extends MenuButton {
      constructor(p, options) {
        super(p, options);
        this.addClass('vjs-quality-btn');
        this.el().querySelector('.vjs-icon-placeholder').innerHTML =
          '<span class="vjs-quality-label">Auto</span>';
      }
      createItems() {
        return qualities.map(q => new QualityMenuItem(this.player(), { label: q, parent: this }));
      }
      buildCSSClass() {
        return `vjs-quality-btn ${super.buildCSSClass()}`;
      }
    }

    player.getChild('controlBar').addChild(new QualityButton(player), {},
      player.getChild('controlBar').children().length - 1
    );

    // Replay & Next buttons
    const Button = videojs.getComponent('Button');

    class ReplayButton extends Button {
      constructor(p, options) {
        super(p, options);
        this.addClass('vjs-replay-btn');
        this.controlText('Replay');
        this.el().innerHTML = `<svg viewBox="0 0 32 32" width="14" height="14">${grad}<rect x="4" y="5" width="3.5" height="22" rx="1.5" fill="${g}"/><path d="M26 5.5L12.5 16 26 26.5V5.5z" fill="${g}"/></svg>`;
      }
      handleClick() {
        this.player().currentTime(0);
        this.player().play();
      }
    }

    class NextButton extends Button {
      constructor(p, options) {
        super(p, options);
        this.addClass('vjs-next-btn');
        this.controlText('Next');
        this.el().innerHTML = `<svg viewBox="0 0 32 32" width="14" height="14">${grad}<path d="M6 5.5L19.5 16 6 26.5V5.5z" fill="${g}"/><rect x="24.5" y="5" width="3.5" height="22" rx="1.5" fill="${g}"/></svg>`;
      }
      handleClick() {
        // Navigate to next related content
        const nextLink = document.querySelector('.cv-related-card');
        if (nextLink) nextLink.click();
      }
    }

    const controlBar = player.getChild('controlBar');
    const playIndex = controlBar.children().indexOf(controlBar.getChild('playToggle'));
    controlBar.addChild(new ReplayButton(player), {}, playIndex + 1);
    controlBar.addChild(new NextButton(player), {}, playIndex + 2);

    const icons = {
      play: `<svg viewBox="0 0 32 32" width="16" height="16">${grad}<path d="M8 4.8c0-1.47 1.6-2.4 2.88-1.68l16.48 9.2c1.3.72 1.3 2.64 0 3.36L10.88 24.88C9.6 25.6 8 24.67 8 23.2V4.8z" fill="${g}"/></svg>`,
      pause: `<svg viewBox="0 0 32 32" width="16" height="16">${grad}<rect x="6" y="4" width="6" height="24" rx="2" fill="${g}"/><rect x="20" y="4" width="6" height="24" rx="2" fill="${g}"/></svg>`,
      volumeHigh: `<svg viewBox="0 0 32 32" width="15" height="15">${grad}<path d="M14 6.5L8.5 11H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h4.5L14 25.5a1 1 0 0 0 1.7-.7V7.2a1 1 0 0 0-1.7-.7z" fill="${g}"/><path d="M20 11c1.7 1.3 2.8 3.3 2.8 5.5S21.7 20.2 20 21.5" stroke="${g}" stroke-width="2.2" stroke-linecap="round" fill="none"/><path d="M23.5 7.5c3 2.5 4.8 6.2 4.8 10s-1.8 7.5-4.8 10" stroke="${g}" stroke-width="2.2" stroke-linecap="round" fill="none"/></svg>`,
      volumeMute: `<svg viewBox="0 0 32 32" width="15" height="15">${grad}<path d="M14 6.5L8.5 11H4a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h4.5L14 25.5a1 1 0 0 0 1.7-.7V7.2a1 1 0 0 0-1.7-.7z" fill="${g}"/><line x1="21" y1="12" x2="29" y2="20" stroke="${g}" stroke-width="2.5" stroke-linecap="round"/><line x1="29" y1="12" x2="21" y2="20" stroke="${g}" stroke-width="2.5" stroke-linecap="round"/></svg>`,
      fullscreen: `<svg viewBox="0 0 32 32" width="15" height="15">${grad}<path d="M4 11V6a2 2 0 0 1 2-2h5" stroke="${g}" stroke-width="2.5" stroke-linecap="round" fill="none"/><path d="M21 4h5a2 2 0 0 1 2 2v5" stroke="${g}" stroke-width="2.5" stroke-linecap="round" fill="none"/><path d="M28 21v5a2 2 0 0 1-2 2h-5" stroke="${g}" stroke-width="2.5" stroke-linecap="round" fill="none"/><path d="M11 28H6a2 2 0 0 1-2-2v-5" stroke="${g}" stroke-width="2.5" stroke-linecap="round" fill="none"/></svg>`,
    };

    // Play/Pause
    const playBtn = controlBar.getChild('playToggle');
    if (playBtn) {
      const playEl = playBtn.el();
      playEl.innerHTML = icons.play;
      player.on('play', () => { playEl.innerHTML = icons.pause; });
      player.on('pause', () => { playEl.innerHTML = icons.play; });
    }

    // Volume
    const volPanel = controlBar.getChild('volumePanel');
    if (volPanel) {
      const muteBtn = volPanel.getChild('muteToggle');
      if (muteBtn) {
        const muteEl = muteBtn.el();
        muteEl.innerHTML = icons.volumeHigh;
        player.on('volumechange', () => {
          muteEl.innerHTML = player.muted() || player.volume() === 0 ? icons.volumeMute : icons.volumeHigh;
        });
      }
    }

    // Fullscreen
    const fsBtn = controlBar.getChild('fullscreenToggle');
    if (fsBtn) {
      fsBtn.el().innerHTML = icons.fullscreen;
    }

    // Thumbnail preview on progress bar hover
    const thumbVideo = document.createElement('video');
    thumbVideo.src = streamUrl;
    thumbVideo.preload = 'auto';
    thumbVideo.muted = true;
    thumbVideo.style.display = 'none';
    document.body.appendChild(thumbVideo);

    const thumbCanvas = document.createElement('canvas');
    thumbCanvas.width = 160;
    thumbCanvas.height = 90;
    const thumbCtx = thumbCanvas.getContext('2d');

    const thumbContainer = document.createElement('div');
    thumbContainer.className = 'vjs-thumb-preview';
    thumbContainer.appendChild(thumbCanvas);
    const thumbTime = document.createElement('span');
    thumbTime.className = 'vjs-thumb-time';
    thumbContainer.appendChild(thumbTime);
    player.el().appendChild(thumbContainer);

    const progressEl = player.el().querySelector('.vjs-progress-control');
    let thumbSeeking = false;

    const onProgressMove = (e) => {
      const rect = progressEl.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      const dur = player.duration();
      if (!dur) return;

      const time = pct * dur;
      const mins = Math.floor(time / 60);
      const secs = Math.floor(time % 60).toString().padStart(2, '0');
      thumbTime.textContent = `${mins}:${secs}`;

      const left = Math.max(85, Math.min(rect.width - 85, e.clientX - rect.left));
      thumbContainer.style.left = left + 'px';
      thumbContainer.style.display = 'block';

      if (!thumbSeeking) {
        thumbSeeking = true;
        thumbVideo.currentTime = time;
      }
    };

    thumbVideo.addEventListener('seeked', () => {
      thumbCtx.clearRect(0, 0, 160, 90);
      thumbCtx.drawImage(thumbVideo, 0, 0, 160, 90);
      thumbSeeking = false;
    });

    progressEl.addEventListener('mousemove', onProgressMove);
    progressEl.addEventListener('mouseleave', () => {
      thumbContainer.style.display = 'none';
    });

    player.on('timeupdate', () => {
      if (canWatchRef.current) return;
      const currentTime = player.currentTime();
      const duration = player.duration();
      if (duration && currentTime / duration >= 0.33) {
        player.pause();
        setPaywalled(true);
      }
    });

    playerRef.current = player;
    setPlayerContainer(player.el());

    return () => {
      thumbVideo.remove();
      setPlayerContainer(null);
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [playing, streamUrl]);

  if (loading) return <div className="loading">Loading...</div>;
  if (!content) return <div className="empty">Content not found</div>;

  const label = TYPE_LABELS[content.type] || content.type;

  const handlePlay = () => {
    setPlaying(true);
  };

  const handleUnlock = () => {
    canWatchRef.current = true;
    setPaywalled(false);
    const player = playerRef.current;
    if (player) setTimeout(() => player.play().catch(() => {}), 100);
  };

  const handleBuy = () => {
    if (!user) { navigate('/login'); return; }
    // Purchase is now handled inside PaywallOverlay via MetaMask
    // This button just scrolls to the player to trigger the paywall
    const el = playerRef.current?.el() || videoRef.current;
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    if (!playing) setPlaying(true);
  };

  const handleComment = (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    const initials = user ? (user.displayName || user.email).slice(0, 2).toUpperCase() : 'TU';
    setComments(prev => [{
      id: Date.now(), user: user?.displayName || 'Tu', avatar: initials,
      time: 'just now', text: commentText.trim()
    }, ...prev]);
    setCommentText('');
  };

  return (
    <div className="cv-page">
      <div className="cv-main">
        {/* Player */}
        <div className="cv-player">
          {playing ? (
            <div className="cv-player-active">
              <>
                <div data-vjs-player>
                  <video ref={videoRef} className="video-js vjs-big-play-centered" />
                </div>
                {paywalled && playerContainer && createPortal(
                  <PaywallOverlay content={content} onUnlock={handleUnlock} />,
                  playerContainer
                )}
              </>
            </div>
          ) : (
            <div className="cv-player-cover" onClick={handlePlay}>
              <img
                src={getThumbnailUrl(content.media_url, content.thumbnail_key, thumbAuth)}
                alt={content.title}
              />
              <button className="cv-play-btn">
                <svg viewBox="0 0 68 68" fill="none">
                  <circle cx="34" cy="34" r="33" fill="rgba(0,0,0,0.7)" stroke="white" strokeWidth="2" />
                  <polygon points="26,20 52,34 26,48" fill="white" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Title & meta */}
        <div className="cv-info">
          <h1 className="cv-title">{content.title}</h1>
          <div className="cv-meta">
            <span className="cv-views">{formatCount(content.viewCount || 0)} views</span>
            <span className="cv-dot">·</span>
            <span className="cv-date">{timeAgo(content.created_at) || ''}</span>
          </div>

          <div className="cv-actions">
            <button
              className={`cv-action-btn ${content.hasLiked ? 'cv-liked' : ''}`}
              onClick={() => {
                if (!user) { navigate('/login'); return; }
                dispatch(toggleLike(id));
              }}
            >
              <svg viewBox="0 0 24 24" fill={content.hasLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
              </svg>
              <span>{content.likeCount || 0}</span>
            </button>
            <button className="cv-action-btn" onClick={() => {
              navigator.clipboard.writeText(window.location.href).then(() => {
                setShowCopied(true);
                setTimeout(() => setShowCopied(false), 2000);
              });
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
              <span>{showCopied ? 'Copied!' : 'Share'}</span>
            </button>
            {canWatch ? (
              <span className="cv-action-btn cv-purchased-badge" title="Purchased">&#10003;</span>
            ) : (
              <button className="cv-action-btn cv-buy-btn" onClick={handleBuy}>
                <span>{parseFloat(parseFloat(content.price_usdc).toFixed(2))} $</span>
              </button>
            )}
          </div>

          {/* Creator */}
          <div className="cv-creator">
            <Link to={content.creator_username ? `/profile/${content.creator_username}` : '#'} className="cv-creator-link">
              <div className="cv-creator-avatar">
                {content.creator_avatar ? (
                  <img src={getAvatarUrl(content.creator_avatar, avatarAuth) || content.creator_avatar} alt="" />
                ) : (
                  (content.creator_name || content.creator || 'C')[0].toUpperCase()
                )}
              </div>
              <div className="cv-creator-info">
                <span className="cv-creator-name">{content.creator_name || content.creator || 'Creator'}</span>
                <span className="cv-creator-subs">{content.creator_username ? `@${content.creator_username}` : 'Creator'}</span>
              </div>
            </Link>
            <button className="cv-subscribe-btn">
              Subscribe
            </button>
            <span className="cv-type-badge">{label}</span>
          </div>

          {/* Description */}
          <div className={`cv-description ${showFullDesc ? 'expanded' : ''}`}>
            <p>{content.description || 'No description available for this content.'}</p>
            {content.description && content.description.length > 100 && (
              <button className="cv-desc-toggle" onClick={() => setShowFullDesc(!showFullDesc)}>
                {showFullDesc ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        </div>

        {/* Comments */}
        <div className="cv-comments">
          <h3 className="cv-comments-title">{comments.length} Comments</h3>

          <form className="cv-comment-form" onSubmit={handleComment}>
            <div className="cv-comment-avatar">
              {user ? (user.displayName || user.email).slice(0, 2).toUpperCase() : 'TU'}
            </div>
            <input
              type="text"
              placeholder="Add a comment..."
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
            />
            {commentText.trim() && (
              <button type="submit" className="cv-comment-submit">Comment</button>
            )}
          </form>

          <div className="cv-comments-list">
            {(showAllComments ? comments : comments.slice(0, 2)).map((c) => (
              <div key={c.id} className="cv-comment">
                <div className="cv-comment-avatar">{c.avatar}</div>
                <div className="cv-comment-body">
                  <div className="cv-comment-header">
                    <span className="cv-comment-user">{c.user}</span>
                    <span className="cv-comment-time">{c.time}</span>
                  </div>
                  <p className="cv-comment-text">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
          {comments.length > 2 && (
            <button className="cv-comments-toggle" onClick={() => setShowAllComments(!showAllComments)}>
              {showAllComments ? 'Hide comments' : `View all ${comments.length} comments`}
            </button>
          )}
        </div>
      </div>

      {/* Sidebar - related */}
      <aside className="cv-sidebar">
        <h3 className="cv-sidebar-title">Related content</h3>
        {related.map((item) => (
          <Link to={`/content/${item.id}`} key={item.id} className="cv-related-card">
            <div className="cv-related-thumb">
              <img src={getThumbnailUrl(item.media_url, item.thumbnail_key, thumbAuth)} alt={item.title} loading="lazy" />
            </div>
            <div className="cv-related-info">
              <h4>{item.title}</h4>
              <span className="cv-related-creator">{item.creator_name || 'Creator'}</span>
              <span className="cv-related-price">{parseFloat(parseFloat(item.price_usdc).toFixed(2))} $</span>
            </div>
          </Link>
        ))}
      </aside>

      {showCopied && <div className="cv-copy-toast">Copied!</div>}
    </div>
  );
}
